import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ArrowRight } from "lucide-react";

/**
 * MarketingPage — modern, consumer-facing layout for Link platform pages
 * surfaced from the TopMenu (How Link Works, Pricing, Service Areas,
 * Safety, Rider Guidelines, About).
 *
 * Optimized for a clean editorial feel: oversized serif title, small
 * orange eyebrow, generous spacing, an optional dark-navy CTA at the
 * end. NOT a settings/legal aesthetic.
 */
export default function MarketingPage({
  eyebrow,
  title,
  lede,
  testId,
  back = "/",
  backLabel = "Back",
  cta = { to: "/", label: "Schedule a ride" },
  showCta = true,
  children,
}) {
  const nav = useNavigate();
  return (
    <div className="fixed inset-0 flex flex-col bg-[#F9F7F4]" data-testid={testId}>
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-3xl mx-auto px-5 sm:px-8 pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        >
          {/* Back chip */}
          <button
            type="button"
            onClick={() => nav(back)}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors mb-5"
            data-testid={`${testId}-back`}
          >
            <ChevronLeft size={14} strokeWidth={2} />
            <span>{backLabel}</span>
          </button>

          {/* Eyebrow + Title + Lede */}
          <div className="text-[10px] tracking-[0.26em] uppercase font-semibold text-[#ed6500] mb-3">
            {eyebrow}
          </div>
          <h1 className="font-serif font-light text-[42px] sm:text-[58px] leading-[1.02] tracking-tight text-[#0A1128]">
            {title}
          </h1>
          {lede && (
            <p className="mt-5 text-[16px] sm:text-[17.5px] leading-relaxed text-[#515E7B] max-w-prose">
              {lede}
            </p>
          )}

          {/* Body */}
          <div className="mt-12 sm:mt-14">{children}</div>

          {/* CTA */}
          {showCta && cta && (
            <div className="mt-16 border-t border-[#E5E1D8] pt-8" data-testid={`${testId}-cta`}>
              <Link
                to={cta.to}
                className="group inline-flex items-center gap-3 bg-[#0A1128] text-[#F9F7F4] px-6 py-4 rounded-2xl text-[12px] tracking-[0.22em] uppercase font-semibold transition-colors hover:bg-[#0A1128]/95"
              >
                <span>{cta.label}</span>
                <ArrowRight size={14} className="text-[#ed6500] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Section — large editorial section with optional micro-eyebrow and
 * supporting paragraph + slot.  Used as the building block within
 * MarketingPage children.
 */
export function MarketingSection({ eyebrow, title, children, testId }) {
  return (
    <section className="mb-14" data-testid={testId}>
      {eyebrow && (
        <div className="text-[10px] tracking-[0.26em] uppercase font-semibold text-[#ed6500] mb-2">
          {eyebrow}
        </div>
      )}
      {title && (
        <h2 className="font-serif text-[28px] sm:text-[34px] leading-tight text-[#0A1128]">
          {title}
        </h2>
      )}
      <div className="mt-4 text-[15px] sm:text-[16px] leading-relaxed text-[#515E7B] max-w-prose space-y-4">
        {children}
      </div>
    </section>
  );
}

/**
 * Pillar — small numbered or icon-led card used in feature grids.
 */
export function Pillar({ Icon, index, title, children, testId }) {
  return (
    <div
      className="bg-white border hairline rounded-2xl p-5 sm:p-6 hover:border-[#ed6500]/40 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        {Icon ? (
          <span className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-[#FCEADE] text-[#ed6500] shrink-0">
            <Icon size={17} strokeWidth={1.9} />
          </span>
        ) : (
          <span className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-[#0A1128] text-[#F9F7F4] font-serif text-[15px] shrink-0 tabular-nums">
            {String(index ?? "·").padStart(2, "0")}
          </span>
        )}
        <h3 className="font-serif text-[20px] text-[#0A1128] leading-tight">{title}</h3>
      </div>
      <div className="mt-3 text-[14px] leading-relaxed text-[#515E7B]">{children}</div>
    </div>
  );
}
